/**
 * Klaviyo "Notify Me" newsletter signup.
 * When a variant is sold out, the sold-out button is replaced by a Notify Me button.
 * Clicking it opens a drawer with an email field. Submissions add the email to
 * a Klaviyo list using the public client-side subscriptions API.
 *
 * After a successful submit for a variant, the button for that variant is greyed
 * out and disabled (per-variant, stored in localStorage).
 */

const KLAVIYO_STORAGE_KEY = 'klaviyo_notified_variants';

function getSubmittedVariants() {
  try {
    const raw = localStorage.getItem(KLAVIYO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function markVariantSubmitted(variantId) {
  if (!variantId) return;
  try {
    const list = getSubmittedVariants();
    const id = String(variantId);
    if (!list.includes(id)) {
      list.push(id);
      localStorage.setItem(KLAVIYO_STORAGE_KEY, JSON.stringify(list));
    }
  } catch (e) {}
}

function isVariantSubmitted(variantId) {
  if (!variantId) return false;
  return getSubmittedVariants().includes(String(variantId));
}

function getCurrentVariantId() {
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get('variant');
  if (fromUrl) return fromUrl;
  const drawer = document.querySelector('klaviyo-notification-drawer');
  return drawer?.dataset.variantId || null;
}

function updateNotifyButtonsState() {
  const buttons = document.querySelectorAll('[data-klaviyo-drawer-target]');
  const currentVariant = getCurrentVariantId();
  const submitted = isVariantSubmitted(currentVariant);
  buttons.forEach((btn) => {
    const label = btn.querySelector('.button__label');
    if (submitted) {
      btn.setAttribute('disabled', 'true');
      btn.classList.add('klaviyo-notification-open-btn--submitted');
      if (label && !label.dataset.originalLabel) {
        label.dataset.originalLabel = label.textContent;
      }
      if (label) label.textContent = 'NOTIFICATION SET';
    } else {
      btn.removeAttribute('disabled');
      btn.classList.remove('klaviyo-notification-open-btn--submitted');
      if (label && label.dataset.originalLabel) {
        label.textContent = label.dataset.originalLabel;
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', updateNotifyButtonsState);
window.addEventListener('popstate', updateNotifyButtonsState);
document.addEventListener('variant:change', updateNotifyButtonsState);

class KlaviyoNotificationDrawer extends HTMLElement {
  constructor() {
    super();

    this.companyId = this.dataset.klaviyoCompanyId;
    this.listId = this.dataset.klaviyoListId;
    this.variantId = this.dataset.variantId;

    this.form = this.querySelector('[data-klaviyo-form]');
    this.submitBtn = this.querySelector('[data-klaviyo-submit]');
    this.spinner = this.querySelector('[data-klaviyo-spinner]');
    this.emailInput = this.querySelector('input[type="email"]');
    this.nameInput = this.querySelector('[data-klaviyo-name]');
    this.errorEl = this.querySelector('[data-klaviyo-error]');
    this.errorTextEl = this.querySelector('[data-klaviyo-error-text]');
    this.successEl = this.querySelector('[data-klaviyo-success]');
    this.overlay = this.querySelector('.klaviyo-notification-overlay');
    this.closeBtn = this.querySelector('.klaviyo-notification-drawer-close');

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.close.bind(this));
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    if (this.emailInput) {
      setTimeout(() => this.emailInput.focus(), 50);
    }
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.resetMessages();
  }

  resetMessages() {
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.successEl) this.successEl.hidden = true;
  }

  setLoading(loading) {
    if (!this.submitBtn) return;
    if (loading) {
      this.submitBtn.setAttribute('disabled', 'true');
      if (this.spinner) this.spinner.classList.remove('hidden');
    } else {
      this.submitBtn.removeAttribute('disabled');
      if (this.spinner) this.spinner.classList.add('hidden');
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    if (this.errorTextEl) this.errorTextEl.textContent = message;
    this.errorEl.hidden = false;
    if (this.successEl) this.successEl.hidden = true;
  }

  showSuccess() {
    if (!this.successEl) return;
    this.successEl.hidden = false;
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.form) this.form.reset();
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.resetMessages();

    const email = this.emailInput?.value.trim();
    if (!email) {
      this.showError('Please enter your email address.');
      return;
    }

    // Basic email format check before we send anything to Klaviyo.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError('Please enter a valid email address.');
      return;
    }

    if (!this.companyId || !this.listId) {
      this.showError('Klaviyo is not configured. Please contact support.');
      return;
    }

    this.setLoading(true);

    // Collect name (optional).
    const name = this.nameInput?.value.trim() || '';
    const firstName = name.split(/\s+/)[0] || '';
    const lastName = name.split(/\s+/).slice(1).join(' ') || '';

    try {
      // Single call: create/update the profile AND add it to the newsletter
      // list in one shot. Klaviyo defaults new list subscriptions to
      // SUBSCRIBED marketing consent when no "subscriptions" override is
      // sent, so this is all that's needed to land them on the list.
      const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`;
      const profileAttrs = { email: email };
      if (firstName) profileAttrs.first_name = firstName;
      if (lastName) profileAttrs.last_name = lastName;

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Notify me form',
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttrs,
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } },
          },
        },
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2025-04-15',
        },
        body: JSON.stringify(body),
      });

      if (response.ok || response.status === 202) {
        markVariantSubmitted(this.variantId);
        updateNotifyButtonsState();
        this.showSuccess();
      } else {
        const errorData = await response.json().catch(() => null);
        const message =
          errorData?.errors?.[0]?.detail ||
          `Subscription failed (${response.status}). Please try again.`;
        this.showError(message);
        console.error('KlaviyoNotificationDrawer: subscribe failed', errorData);
      }
    } catch (err) {
      this.showError('Network error. Please try again.');
      console.error('KlaviyoNotificationDrawer: request failed', err);
    } finally {
      this.setLoading(false);
    }
  }

  // Klaviyo onsite JS is loaded async. Poll briefly for _learnq to appear.
  waitForKlaviyo(timeoutMs = 3000) {
    return new Promise((resolve) => {
      if (window._learnq) return resolve(window._learnq);
      const start = Date.now();
      const check = setInterval(() => {
        if (window._learnq) {
          clearInterval(check);
          resolve(window._learnq);
        } else if (Date.now() - start > timeoutMs) {
          clearInterval(check);
          resolve(null);
        }
      }, 50);
    });
  }
}

customElements.define('klaviyo-notification-drawer', KlaviyoNotificationDrawer);

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-klaviyo-drawer-target]');
  if (!trigger) return;
  if (trigger.hasAttribute('disabled')) return;
  const targetId = trigger.getAttribute('data-klaviyo-drawer-target');
  const drawer = document.getElementById(targetId);
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
});
